import { useState, useMemo, useRef, useEffect } from "react";
import { useAttribution } from "@/hooks/useAttribution";
import { useLocation } from "wouter";
import { Heart, MessageCircle, Share2, ThumbsUp, ThumbsDown, MapPin, Star, Bookmark, ShoppingCart, ChevronLeft, ChevronRight, ChevronDown, Flag, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { KudozIcon } from "@/components/KudozIcon";
import OrderInitiationModal from "./OrderInitiationModal";
import ShareReviewModal from "./ShareReviewModal";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { CommentsSheet } from "./CommentsSheet";
import { ReportModal } from "./ReportModal";
import RelatedReviewsSheet from "./RelatedReviewsSheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { resolveEatzyMediaUrl } from "@/lib/utils";
import { AuthenticatedVideo } from "./AuthenticatedVideo";
import UserAvatar from "./UserAvatar";
import TrustBadge, { getTrustTier, TIER_CONFIG, getTierPerks } from "./TrustBadge";
import ItemRating, { type ItemRatingValue } from "./ItemRating";
import { cn } from "@/lib/utils";
import { CardContent } from "./ui/card";
import { useToast } from "@/hooks/use-toast";
import FollowButton from "./FollowButton";
import { LazyImage } from "./LazyImage";
import { useTags } from "@/hooks/useTags";
import { MediaLightbox, type LightboxEntry } from "./MediaLightbox";

const KB_PRESETS = [
  { name: 'kb-pan-right',   duration: 8 },
  { name: 'kb-pan-left',    duration: 8 },
  { name: 'kb-pan-down',    duration: 7 },
  { name: 'kb-pan-up',      duration: 7 },
  { name: 'kb-zoom-in',     duration: 9 },
  { name: 'kb-zoom-out',    duration: 9 },
  { name: 'kb-zoom-pan-tr', duration: 10 },
  { name: 'kb-zoom-pan-bl', duration: 10 },
];

interface ReviewCardProps {
  id: string;
  isOwner?: boolean; // true when the current user owns this review — shows edit/delete controls
  isPartnerPost?: boolean; // true for restaurant_post (QuickPost), fires post_view instead of review_view
  user: {
    id?: string; // User ID for follow functionality
    name: string;
    displayName?: string; // First Name + Last Initial (e.g., "Sarah C.")
    avatar?: string;
    verified?: boolean;
    trustScore?: number; // 0-100 composite trust score
    followersCount?: number;
    reviewsCount?: number;
    isFollowing?: boolean;
  };
  restaurant: {
    id: string;
    name: string;
    location: string;
    city: string;
    overallRating: number;
    totalReviews: number;
    doubleKudoz?: boolean;
    orderingEnabled?: boolean;
    restaurantImage?: string;
  };
  dish?: {
    name: string;
    price?: number;
  };
  items?: ReviewItem[];
  rating: number;
  itemRating?: ItemRatingValue;
  text: string;
  images?: string[];
  image?: string;
  video?: string;
  createdAt: string;
  likes: number;
  comments: number;
  helpfulUpVotes: number;
  helpfulDownVotes: number;
  kudozEarned: number;
  tags: string[];
  isLiked?: boolean;
  isHelpful?: boolean | null;
  isBookmarked?: boolean;
  relatedReviews?: ReviewCardProps[];
  allReviews?: ReviewCardProps[];
}

interface ReviewItem {
  name: string;
  price: number;
  image: string;
  isItemVideo?: boolean;
  itemRating: ItemRatingValue;
  itemRatingText?: string;
  averageRating: number;
  totalRatings: number;
  isCrowdFavorite: boolean;
  isTheBest: boolean;
}

export default function ReviewCard({ 
  id,
  isOwner = false,
  isPartnerPost = false,
  user, 
  restaurant, 
  dish,
  items,
  rating: initialRating, 
  itemRating,
  text: initialText, 
  images,
  image,
  video,
  createdAt, 
  likes, 
  comments, 
  helpfulUpVotes, 
  helpfulDownVotes,
  kudozEarned,
  tags,
  isLiked = false,
  isHelpful = null,
  isBookmarked = false,
  relatedReviews = [],
  allReviews = []
}: ReviewCardProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [helpful, setHelpful] = useState<boolean | null>(isHelpful);
  const [upVotes, setUpVotes] = useState(helpfulUpVotes);
  const [downVotes, setDownVotes] = useState(helpfulDownVotes);
  const [bookmarked, setBookmarked] = useState(isBookmarked);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTrustModal, setShowTrustModal] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showRelatedSheet, setShowRelatedSheet] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Optimistic local state for rating/text so edits reflect immediately
  const [rating, setRating] = useState(initialRating);
  const [text, setText] = useState(initialText);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRating, setEditRating] = useState(initialRating);
  const [editText, setEditText] = useState(initialText);

  // Delete confirm dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: async (data: { rating: number; text: string }) => {
      const res = await apiRequest('PUT', `/api/reviews/${id}`, data);
      if (!res.ok) throw new Error('Failed to update review');
      return res.json();
    },
    onSuccess: (updated) => {
      setRating(updated.rating ?? rating);
      setText(updated.text ?? text);
      setShowEditModal(false);
      queryClient.invalidateQueries({ queryKey: ['/api/reviews/me'] });
      toast({ description: 'Review updated successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to update review', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('DELETE', `/api/reviews/${id}`);
      if (!res.ok) throw new Error('Failed to delete review');
      return res.json();
    },
    onSuccess: () => {
      setShowDeleteConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['/api/reviews/me'] });
      toast({ description: 'Review deleted' });
    },
    onError: () => {
      toast({ description: 'Failed to delete review', variant: 'destructive' });
    },
  });

  // Ken Burns: pick a random preset each time the visible image changes
  const kbPresetRef = useRef<{ name: string; duration: number } | null>(null);
  const kbLastIndexRef = useRef<number>(-1);
  if (kbLastIndexRef.current !== currentImageIndex || kbPresetRef.current === null) {
    kbLastIndexRef.current = currentImageIndex;
    kbPresetRef.current = KB_PRESETS[Math.floor(Math.random() * KB_PRESETS.length)];
  }
  const kbStyle = {
    animationName: kbPresetRef.current.name,
    animationDuration: `${kbPresetRef.current.duration}s`,
    animationIterationCount: 'infinite',
    animationDirection: 'alternate' as const,
  };

  // Attribution tracking: fire post_view (partner posts) or review_view on mount
  const { sendAttributionEvent } = useAttribution();
  useEffect(() => {
    if (isPartnerPost) {
      sendAttributionEvent('post_view', 'post', id);
    } else {
      sendAttributionEvent('review_view', 'review', id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Get tag data to identify cuisine tags
  const { allTags } = useTags();
  
  // Separate cuisine tags from other tags
  const { cuisineTags, otherTags } = useMemo(() => {
    if (!allTags.length || !tags.length) {
      return { cuisineTags: [] as string[], otherTags: tags };
    }
    
    // Create a set of cuisine tag names for fast lookup
    const cuisineTagNames = new Set(
      allTags
        .filter(t => t.type === 'cuisine')
        .map(t => t.name.toLowerCase())
    );
    
    const cuisine: string[] = [];
    const other: string[] = [];
    
    tags.forEach(tag => {
      if (cuisineTagNames.has(tag.toLowerCase())) {
        cuisine.push(tag);
      } else {
        other.push(tag);
      }
    });
    
    return { cuisineTags: cuisine, otherTags: other };
  }, [tags, allTags]);

  // Fetch restaurant status to determine if it's open
  // Pre-fetch for ordering-enabled restaurants so status is ready when modal opens
  const { data: restaurantData, isLoading: restaurantLoading } = useQuery<{ 
    liveStatus?: { open: boolean };
    eatzyTimezone?: string | null;
    hoursJson?: Record<string, unknown> | null;
  }>({
    queryKey: ['/api/restaurants', restaurant.id],
    enabled: restaurant.orderingEnabled === true
  });
  // Default to undefined while loading to show loading state in modal
  // Don't default to true when data is missing - let the modal handle undefined state
  const isRestaurantOpen = restaurantLoading ? undefined : restaurantData?.liveStatus?.open;

  // Handler for order modal proceed
  const handleOrderProceed = (settings: {
    serviceType: 'delivery' | 'takeout';
    timing: 'asap' | 'scheduled';
    scheduledTime?: string;
    location: string;
    coordinates?: { lat: number; lng: number };
  }) => {
    // Build URL with order context parameters - match Restaurant page's expected param names
    const params = new URLSearchParams();
    params.set('serviceType', settings.serviceType);
    params.set('timing', settings.timing);
    params.set('location', settings.location);
    if (settings.scheduledTime) {
      params.set('scheduledTime', settings.scheduledTime);
    }
    if (settings.coordinates) {
      params.set('lat', settings.coordinates.lat.toString());
      params.set('lng', settings.coordinates.lng.toString());
    }
    
    // Navigate to restaurant page with order context
    navigate(`/restaurant/${restaurant.id}?${params.toString()}`);
  };
  
  // Build combined media list: item images first (with item reference), then overall experience images
  const getReviewMediaWithMapping = () => {
    const mediaEntries: Array<{ url: string; item?: ReviewItem; isVideo?: boolean }> = [];

    const resolve = (u: string) => resolveEatzyMediaUrl(u) || u;

    // Add item images first — each mapped to its item for overlay.
    // Items whose media is a video (isItemVideo=true, or inferred from URL extension) are pushed
    // with isVideo:true so the carousel renders them as <video> and the overlay still shows.
    const looksLikeVideo = (url: string) =>
      /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url);

    if (items && items.length > 0) {
      for (const item of items) {
        if (item.image) {
          const isVideo = item.isItemVideo === true || looksLikeVideo(item.image);
          mediaEntries.push({ url: resolve(item.image), item, isVideo });
        }
      }
    }

    // Add overall experience images (no item mapping — no overlay for these)
    if (images && images.length > 0) {
      for (const img of images) {
        if (img) {
          mediaEntries.push({ url: resolve(img) });
        }
      }
    }

    // Add standalone video (not claimed by any item) after photos
    if (video) {
      mediaEntries.push({ url: resolve(video), isVideo: true });
    }

    // Fall back to single image if nothing else
    if (mediaEntries.length === 0 && image) {
      mediaEntries.push({ url: resolve(image) });
    }

    // Final fallback: restaurant's own image (when reviewer submitted no media)
    if (mediaEntries.length === 0 && restaurant.restaurantImage) {
      mediaEntries.push({ url: resolve(restaurant.restaurantImage) });
    }

    return mediaEntries;
  };
  const reviewMediaEntries = getReviewMediaWithMapping();
  const reviewImages = reviewMediaEntries.map(e => e.url);
  const hasMultipleImages = reviewImages.length > 1;
  // Current item overlay — only show when the current carousel image belongs to a specific item
  const currentItem = reviewMediaEntries[currentImageIndex]?.item || null;

  // Photo-only entries for the lightbox (videos are excluded).
  // Falls back to the top-level legacy `dish`/`itemRating` props when a
  // photo has no specific item mapping, so overlay parity matches the card.
  const lightboxEntries: LightboxEntry[] = reviewMediaEntries
    .filter(e => !e.isVideo)
    .map(e => ({
      url: e.url,
      isVideo: false,
      item: e.item
        ? {
            name: e.item.name,
            price: e.item.price,
            itemRating: e.item.itemRating,
            itemRatingText: e.item.itemRatingText,
            isTheBest: e.item.isTheBest,
          }
        : (dish && itemRating
            ? { name: dish.name, price: dish.price ?? 0, itemRating, itemRatingText: undefined, isTheBest: false }
            : null),
    }));
  
  // Generate related reviews if not provided
  const getRelatedReviews = () => {
    if (relatedReviews.length > 0) return relatedReviews;
    if (allReviews.length === 0) return [];
    
    // Find reviews from same restaurant (excluding current review)
    return allReviews
      .filter(review => 
        review.id !== id && 
        review.restaurant.name === restaurant.name
      )
      .slice(0, 3);
  };
  
  const computedRelatedReviews = getRelatedReviews();

  const handleHelpful = (vote: boolean) => {
    console.log(`Helpful vote: ${vote ? 'up' : 'down'}`);
    const previousHelpful = helpful;
    
    // Update vote counts based on the action
    if (previousHelpful === vote) {
      // Removing the vote
      if (vote) {
        setUpVotes(upVotes - 1);
      } else {
        setDownVotes(downVotes - 1);
      }
      setHelpful(null);
    } else if (previousHelpful === null) {
      // Adding a new vote
      if (vote) {
        setUpVotes(upVotes + 1);
      } else {
        setDownVotes(downVotes + 1);
      }
      setHelpful(vote);
    } else {
      // Switching from one vote to another
      if (vote) {
        setDownVotes(downVotes - 1);
        setUpVotes(upVotes + 1);
      } else {
        setUpVotes(upVotes - 1);
        setDownVotes(downVotes + 1);
      }
      setHelpful(vote);
    }
  };

  const handleComment = () => {
    setShowComments(true);
  };

  const handleShare = () => {
    console.log('Share triggered');
    setShowShareModal(true);
  };

  const handleBookmark = () => {
    const newBookmarkedState = !bookmarked;
    setBookmarked(newBookmarkedState);
    
    // Check if this is the user's first save (for Kudoz bonus)
    const hasEarnedFirstSaveBonus = localStorage.getItem('hasEarnedFirstSaveBonus');
    
    if (newBookmarkedState) {
      if (!hasEarnedFirstSaveBonus) {
        // First save bonus!
        localStorage.setItem('hasEarnedFirstSaveBonus', 'true');
        toast({
          description: (
            <div className="flex items-center gap-2">
              <Bookmark className="w-4 h-4 fill-current text-kudoz" />
              <div>
                <div className="font-medium">Saved to your list!</div>
                <div className="text-sm text-muted-foreground">+10 Kudoz bonus for your first save</div>
              </div>
            </div>
          )
        });
      } else {
        toast({
          description: (
            <div className="flex items-center gap-2">
              <Bookmark className="w-4 h-4 fill-current text-kudoz" />
              <span>Saved {restaurant.name}</span>
            </div>
          )
        });
      }
    } else {
      toast({
        description: `Removed ${restaurant.name} from saved`
      });
    }
  };

  const handleOrder = () => {
    setShowOrderModal(true);
  };

  // Calculate trust score and derive tier from composite system
  const userTrustScore = user.trustScore || 75;
  const userTrustTier = getTrustTier(userTrustScore);

  return (
    <Card className="w-full max-w-sm mx-auto mb-4 overflow-hidden hover-elevate" data-testid="review-card" data-review-id={id}>
      {/* Three-line Header: Line 1 = Reviewer, Line 2 = Restaurant Name, Line 3 = City + Rating */}
      <div className="p-2.5 pb-1.5">
        <div className="flex items-start gap-2">
          <UserAvatar 
            src={user.avatar} 
            name={user.name} 
            size="sm" 
            verified={false}
            trustScore={userTrustScore}
          />
          <div className="flex-1 min-w-0">
            {/* Line 1: Reviewer Name + Trust Badge + Follow (at end) */}
            <div className="flex items-center gap-1 text-sm min-w-0">
              <button
                onClick={() => navigate(user.id ? `/profile/${user.id}` : `/profile`)}
                className="hover:underline font-semibold truncate"
                data-testid="link-reviewer-name"
              >
                {user.displayName || user.name}
              </button>
              <TrustBadge 
                trustScore={userTrustScore}
                trustTier={userTrustTier}
                size="sm"
                showScore={false}
                showLabel={false}
                onClick={() => setShowTrustModal(true)}
                data-testid="trust-badge-reviewer"
              />
              {/* 2x Kudoz badge */}
              {restaurant.doubleKudoz && (
                <Badge 
                  variant="outline" 
                  className="text-xs px-1.5 py-0.5 text-kudoz border-kudoz/50 bg-kudoz/10 flex items-center gap-0.5 flex-shrink-0"
                  data-testid="kudoz-promo-badge"
                >
                  <KudozIcon size="xs" />
                  <span className="hidden sm:inline">2x</span>
                </Badge>
              )}
              <div className="flex-1" />
              {/* Owner actions menu OR follow button */}
              {isOwner ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0 text-muted-foreground"
                      aria-label="Review options"
                      data-testid="button-review-options"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        setEditRating(rating);
                        setEditText(text);
                        setShowEditModal(true);
                      }}
                      data-testid="menu-item-edit-review"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit Review
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-destructive focus:text-destructive"
                      data-testid="menu-item-delete-review"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Review
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                user.id && (
                  <FollowButton
                    targetUserId={user.id}
                    size="icon"
                    variant="ghost"
                    showText={false}
                    className="flex-shrink-0"
                  />
                )
              )}
            </div>
            {/* Line 2: Restaurant Name + Bookmark (at end) */}
            <div className="flex items-center gap-1 text-sm min-w-0 -mt-1">
              <button
                onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                className="hover:underline font-bold truncate"
                data-testid="link-restaurant-name"
              >
                {restaurant.name}
              </button>
              <div className="flex-1" />
              {/* Bookmark button at end of line */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBookmark}
                  className={cn(
                    "flex-shrink-0",
                    bookmarked ? "text-kudoz" : "text-muted-foreground"
                  )}
                  data-testid="button-bookmark"
                  aria-label={bookmarked ? "Remove from saved" : "Save restaurant"}
                >
                  <Bookmark className={cn("w-4 h-4", bookmarked && "fill-current")} />
                </Button>
            </div>
            {/* Line 3: City + Cuisine Tags + Rating (compact, no button) */}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground -mt-1.5">
              <span className="truncate">{restaurant.city}</span>
              {cuisineTags.length > 0 && (
                <>
                  <span className="flex-shrink-0">·</span>
                  <span className="text-foreground/80 truncate">
                    {cuisineTags.slice(0, 2).join(', ')}
                  </span>
                </>
              )}
              <span className="flex-shrink-0">·</span>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Star className="w-3 h-3 fill-rating text-rating" />
                <span className="font-medium text-foreground">{restaurant.overallRating?.toFixed(1) || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Section - Rating inline with text */}
      <div className="px-4 pb-3 pt-2">
        <div className="bg-accent/30 rounded-md p-3 border border-accent/50">
          <p className="text-sm text-foreground leading-relaxed">
            <span className="inline-flex items-center gap-1 mr-1.5">
              <Star className="w-4 h-4 fill-rating text-rating" />
              <span className="font-bold text-base">{rating.toFixed(1)}</span>
            </span>
            <span className="text-muted-foreground mx-1">—</span>
            <span className="italic">"{text}"</span>
          </p>
        </div>
      </div>

      {/* Image/Video Carousel - 16:9 for more visual impact */}
      {reviewImages.length > 0 && (
        <div className="relative aspect-video bg-muted overflow-visible">
          <div className="w-full h-full overflow-hidden">
            {reviewMediaEntries[currentImageIndex]?.isVideo ? (
              <AuthenticatedVideo 
                src={reviewImages[currentImageIndex]} 
                className="w-full h-full"
                data-testid="review-video"
                socialControls
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <button
                className="w-full h-full block cursor-zoom-in focus:outline-none"
                aria-label="View photo fullscreen"
                onClick={() => {
                  if (lightboxEntries.length === 0) return;
                  const photoIndex = reviewMediaEntries
                    .slice(0, currentImageIndex + 1)
                    .filter(e => !e.isVideo).length - 1;
                  setLightboxIndex(Math.max(0, photoIndex));
                  setLightboxOpen(true);
                }}
              >
                <LazyImage 
                  src={reviewImages[currentImageIndex]} 
                  alt="Review" 
                  className="w-full h-full"
                  imgClassName="kb-anim"
                  imgStyle={kbStyle}
                  data-testid="review-image"
                />
              </button>
            )}
          </div>
          
          {/* Item name and rating overlay */}
          {currentItem && (
            <div className={cn("absolute top-3 left-3", restaurant.orderingEnabled ? "right-32" : "right-3")}>
              <div className="bg-black/70 backdrop-blur-sm rounded-md px-3 py-1.5 max-w-full">
                {/* Item name and price on same line */}
                <div className="flex items-baseline gap-1.5">
                  <div className="text-white text-sm font-medium truncate">{currentItem.name}</div>
                  {currentItem.price > 0 && (
                    <div className="text-white/75 text-xs flex-shrink-0">${currentItem.price.toFixed(2)}</div>
                  )}
                </div>
                
                {/* Item rating icon and text on same line */}
                <div className="flex items-center gap-1.5 mt-1">
                  <ItemRating rating={currentItem.itemRating} size="sm" />
                  {currentItem.itemRatingText && (
                    <div className="text-white/90 text-xs italic flex-1 truncate">
                      "{currentItem.itemRatingText}"
                    </div>
                  )}
                  {currentItem.isTheBest && (
                    <Badge variant="secondary" className="text-xs h-5 bg-yellow-500/90 text-black border-0 flex-shrink-0">
                      The Best
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Fallback for old data structure */}
          {!currentItem && dish && itemRating && (
            <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm rounded-md px-3 py-1.5">
              <div className="text-white text-sm font-medium mb-0.5">{dish.name}</div>
              <div className="flex items-center gap-1">
                <ItemRating rating={itemRating} size="sm" />
              </div>
            </div>
          )}
          
          {/* Image Navigation Arrows */}
          {hasMultipleImages && (
            <>
              {/* Previous Button - Full height tap area */}
              {currentImageIndex > 0 && (
                <button
                  onClick={() => setCurrentImageIndex(prev => prev - 1)}
                  className="absolute left-0 top-0 bottom-0 w-20 flex items-center justify-start pl-3 bg-gradient-to-r from-black/20 to-transparent hover:from-black/40 transition-all"
                  data-testid="button-prev-image"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-lg">
                    <ChevronLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                  </div>
                </button>
              )}
              
              {/* Next Button - Full height tap area */}
              {currentImageIndex < reviewImages.length - 1 && (
                <button
                  onClick={() => setCurrentImageIndex(prev => prev + 1)}
                  className="absolute right-0 top-0 bottom-0 w-20 flex items-center justify-end pr-3 bg-gradient-to-l from-black/20 to-transparent hover:from-black/40 transition-all"
                  data-testid="button-next-image"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-lg">
                    <ChevronRight className="w-5 h-5 text-gray-900 dark:text-white" />
                  </div>
                </button>
              )}
              
              {/* Image Indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {reviewImages.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-1.5 h-1.5 rounded-full transition-all",
                      index === currentImageIndex 
                        ? "bg-white w-4" 
                        : "bg-white/60 hover:bg-white/80"
                    )}
                    onClick={() => setCurrentImageIndex(index)}
                    data-testid={`image-indicator-${index}`}
                  />
                ))}
              </div>
            </>
          )}
          
          {/* Tags Overlay - Bottom left of image (non-cuisine tags only) */}
          {otherTags.length > 0 && (
            <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 max-w-[60%]">
              {otherTags.slice(0, 3).map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-0.5 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full"
                  data-testid={`tag-${tag}`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Order Button Overlay - Always top right of image */}
          {restaurant.orderingEnabled && (
            <Button
              variant="default"
              size="sm"
              onClick={handleOrder}
              className="shadow-lg z-30"
              style={{ position: 'absolute', top: '12px', right: '12px' }}
              data-testid="button-order"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              <span className="text-sm font-medium">Order</span>
            </Button>
          )}
        </div>
      )}

      {/* No-media fallback: branded text-quote card */}
      {reviewImages.length === 0 && (
        <div
          className="relative aspect-video flex flex-col overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0D9C97 0%, #0b8a85 45%, #c4780e 75%, #F9A01B 100%)' }}
        >
          {/* Subtle dot-grid texture */}
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 0)',
              backgroundSize: '22px 22px',
            }}
          />

          {/* Centre content */}
          <div className="relative flex flex-col items-center justify-center flex-1 px-6 py-5 text-center gap-3">
            {/* Star + rating */}
            <div className="flex items-center gap-1.5">
              <Star className="w-5 h-5 fill-white text-white drop-shadow" />
              <span className="text-white font-bold text-2xl leading-none drop-shadow">{rating.toFixed(1)}</span>
            </div>

            {/* Review quote */}
            <p className="text-white/95 text-sm italic leading-relaxed line-clamp-3 max-w-xs drop-shadow-sm">
              &ldquo;{text}&rdquo;
            </p>

            {/* Restaurant name */}
            <p className="text-white/70 text-xs font-semibold tracking-widest uppercase">
              {restaurant.name}
            </p>
          </div>

          {/* Tags row */}
          {otherTags.length > 0 && (
            <div className="relative pb-3 px-3 flex flex-wrap gap-1 justify-center">
              {otherTags.slice(0, 4).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 bg-black/25 backdrop-blur-sm text-white text-xs rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Item list — always visible; image overlay on carousel is additive, not the only path */}
      {items && items.length > 0 && (
        <div className="px-3 pb-2 space-y-1.5 border-t border-border/40 pt-2 mt-1">
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <ItemRating rating={item.itemRating} size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5 flex-wrap gap-y-0.5">
                  <span className="text-sm font-medium leading-tight">{item.name}</span>
                  {item.price > 0 && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      ${item.price.toFixed(2)}
                    </span>
                  )}
                  {item.isTheBest && (
                    <Badge
                      variant="secondary"
                      className="text-xs h-4 bg-yellow-500/90 text-black border-0 flex-shrink-0"
                    >
                      The Best
                    </Badge>
                  )}
                </div>
                {item.itemRatingText && (
                  <p className="text-xs text-muted-foreground truncate italic">
                    &ldquo;{item.itemRatingText}&rdquo;
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Button Banner - Fallback for reviews without images */}
      {reviewImages.length === 0 && restaurant.orderingEnabled && (
        <div className="px-3 pb-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleOrder}
            className="w-full h-9"
            data-testid="button-order"
          >
            <ShoppingCart className="w-4 h-4 mr-1.5" />
            <span className="text-sm font-medium">Order from {restaurant.name}</span>
          </Button>
        </div>
      )}

      {/* Content */}
      <div className="p-3 pt-2">
        {/* Compact Action Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Helpful - Compact inline */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpful(true)}
                className={cn(
                  "p-0 h-auto",
                  helpful === true ? "text-kudoz" : "text-muted-foreground hover:text-foreground"
                )}
                data-testid="button-helpful-up"
              >
                <ThumbsUp className={cn("w-4 h-4", helpful === true && "fill-current")} />
              </Button>
              <span className="text-xs text-muted-foreground">{upVotes}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpful(false)}
                className={cn(
                  "p-0 h-auto",
                  helpful === false ? "text-destructive" : "text-muted-foreground hover:text-foreground"
                )}
                data-testid="button-helpful-down"
              >
                <ThumbsDown className={cn("w-4 h-4", helpful === false && "fill-current")} />
              </Button>
            </div>

            {/* Share */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleShare}
              className="p-0 h-auto text-muted-foreground hover:text-foreground"
              data-testid="button-share"
            >
              <Share2 className="w-4 h-4" />
            </Button>

            {/* Comment */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleComment}
              className="flex items-center gap-1 p-0 h-auto text-muted-foreground hover:text-foreground"
              data-testid="button-comment"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs">{comments}</span>
            </Button>

            {/* Report */}
            <Button
              variant="ghost" 
              size="sm"
              onClick={() => setShowReportModal(true)}
              className="p-0 h-auto text-muted-foreground hover:text-destructive"
              data-testid="button-report-review"
              aria-label="Report review"
            >
              <Flag className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Right side: Timestamp only (Follow/Bookmark moved inline with names) */}
          <div className="flex items-center">
            <span className="text-xs text-muted-foreground">{createdAt}</span>
          </div>
        </div>
        
        {/* Related Reviews - Compact Badge */}
        {computedRelatedReviews.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <button
              onClick={() => setShowRelatedSheet(true)}
              className="w-full flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors hover-elevate active-elevate-2 border border-border/50"
              data-testid="related-reviews-badge"
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {computedRelatedReviews.slice(0, 3).map((review, index) => (
                    <div
                      key={review.id}
                      className="relative"
                      style={{ zIndex: 3 - index }}
                    >
                      <UserAvatar
                        src={review.user.avatar}
                        name={review.user.name}
                        size="sm"
                        className="ring-2 ring-background"
                      />
                    </div>
                  ))}
                </div>
                <span className="text-sm font-medium text-foreground">
                  +{computedRelatedReviews.length} more review{computedRelatedReviews.length > 1 ? 's' : ''} from this spot
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        )}
      </div>
      
      <OrderInitiationModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        onProceed={handleOrderProceed}
        restaurantId={restaurant.id}
        mode="order"
        isRestaurantOpen={isRestaurantOpen}
        timezone={restaurantData?.eatzyTimezone}
        hoursJson={restaurantData?.hoursJson}
      />
      
      <ShareReviewModal 
        open={showShareModal}
        onOpenChange={setShowShareModal}
        reviewId={id}
        restaurantName={restaurant.name}
      />

      <CommentsSheet
        reviewId={id}
        isOpen={showComments}
        onClose={() => setShowComments(false)}
      />
      
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        targetType="review"
        targetId={id}
      />

      <RelatedReviewsSheet
        isOpen={showRelatedSheet}
        onClose={() => setShowRelatedSheet(false)}
        restaurantName={restaurant.name}
        reviews={computedRelatedReviews}
        onReviewClick={(reviewId) => {
          const reviewElement = document.querySelector(`[data-testid="review-card"][data-review-id="${reviewId}"]`);
          if (reviewElement) {
            reviewElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />
      
      {/* Trust Rating System Modal */}
      <Dialog open={showTrustModal} onOpenChange={setShowTrustModal}>
        <DialogContent className="max-w-lg max-h-[90svh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Trust Rating System</DialogTitle>
            <DialogDescription>
              Trust ratings help you identify the most reliable reviewers in your network
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* How It Works */}
            <div>
              <h3 className="font-semibold text-base mb-3">How Trust Ratings Work</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Trust ratings are calculated based on:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Helpful votes on reviews</li>
                  <li>Trust votes from other users</li>
                  <li>Number of followers</li>
                  <li>Review quality and consistency</li>
                </ul>
                <p className="mt-3">
                  Scores range from <span className="font-semibold text-foreground">0-100</span>, with higher scores indicating more trustworthy reviewers.
                </p>
              </div>
            </div>

            {/* Trust Tiers */}
            <div>
              <h3 className="font-semibold text-base mb-3">Trust Tiers</h3>
              <div className="space-y-3">
                {(['newbie', 'reliable', 'trusted', 'elite'] as const).map((tier) => {
                  const config = TIER_CONFIG[tier];
                  const [min, max] = config.range;
                  const exampleScore = min + Math.floor((max - min) / 2);
                  const perks = getTierPerks(tier);

                  return (
                    <Card key={tier} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <TrustBadge
                            trustScore={exampleScore}
                            trustTier={tier}
                            size="md"
                            showScore={true}
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-base mb-1">{config.label}</div>
                            <div className="text-sm text-muted-foreground">
                              Score range: {min}-{max}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <p className="mb-2">{config.description}</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {perks.map((perk, i) => (
                              <li key={i}>{perk}</li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MediaLightbox
        entries={lightboxEntries}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Edit Review Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Review</DialogTitle>
            <DialogDescription>
              Update your rating or review text.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-rating">Rating (1–10)</Label>
              <div className="flex items-center gap-3">
                <input
                  id="edit-rating"
                  type="range"
                  min={1}
                  max={10}
                  step={0.5}
                  value={editRating}
                  onChange={(e) => setEditRating(parseFloat(e.target.value))}
                  className="flex-1 accent-kudoz"
                  data-testid="edit-rating-slider"
                />
                <span className="text-sm font-bold w-8 text-right">{editRating.toFixed(1)}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-text">Review Text</Label>
              <Textarea
                id="edit-text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Share your experience..."
                className="resize-none"
                data-testid="edit-review-text"
              />
              <p className="text-xs text-muted-foreground text-right">{editText.length}/2000</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              disabled={editMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => editMutation.mutate({ rating: editRating, text: editText })}
              disabled={editMutation.isPending}
              data-testid="button-confirm-edit-review"
            >
              {editMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Review</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this review? Any Kudoz earned from it will be reversed — including Kudoz already credited to your wallet. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-review"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}